import { Container, Divider } from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import { Grid } from "@mui/material";
import axios from "axios";
import {
  ArrayChip,
  ButtonGroupTop,
  ModalError,
  ModalSuccess,
  Page,
  PrimaryButton,
  SearchTextInput,
  SkeletonComponent,
  StatusChip,
  TableAction,
} from "components";
import { InnoTableV2 } from "inno-ui";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { formatDate, getErrors } from "utils";
import ModalFormParameterGroup from "./Components/ModalFormParameterGroup";

function ParameterGroup() {
  const history = useHistory();
  const location = useLocation();
  const classes = globalStyles();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const urlParams = new URLSearchParams(location.search);
  const paramsSearch = urlParams?.get("search");
  const paramsPage = urlParams?.get("page");
  const paramsSize = urlParams?.get("size");
  const [loadingPage, setLoadingPage] = useState(false);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: Number(paramsPage) || 1,
    size: Number(paramsSize) || 10,
    search: paramsSearch || "",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalPreload, setModalPreload] = useState(null);

  const handleChangePageParams = (value, key) => {
    urlParams.set(key, value);
    history.replace({
      pathname: location.pathname,
      search: urlParams.toString(),
    });
  };
  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
    handleChangePageParams(value, key);
    if (key !== "page") {
      handleChangePageParams(1, "page");
    }
  };

  const getDataTable = async () => {
    try {
      setLoadingPage(true);
      const res = await axios.get(`${hardBaseUrl}/publisher/group/index`, {
        headers,
        params: queryParams,
      });
      const { data, meta } = res?.data;
      setDataTable(data || []);
      const pageCount = getTotalPage(meta?.total, queryParams?.size || 1);
      setTableTotalPage(pageCount);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const debounceDataTable = useCallback(
    debounce(() => {
      getDataTable();
    }, 500),
    [queryParams]
  );
  const handleDelete = async value => {
    try {
      setLoadingPage(true);
      await axios.delete(
        `${hardBaseUrl}/publisher/group/delete/${value?.publisher_group_id}`,
        { headers }
      );
      ModalSuccess("Data Deleted Successfully").then(() => getDataTable());
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };

  useEffect(() => {
    const isSearching = Boolean(queryParams?.search);
    if (isSearching) {
      debounceDataTable();
      return () => {
        debounceDataTable.cancel();
      };
    } else {
      getDataTable();
    }
  }, [queryParams, debounceDataTable]);

  return (
    <Page className={classes.root} title="Group">
      <Container maxWidth={false}>
        <HeaderTitle title="Parameter" breadcrumbData={breadcrumbData} />
        <ButtonGroupTop />
        <Divider className={classes.flatDivider} />
        <Grid container justifyContent="space-between" my="16px">
          <Grid item>
            <SearchTextInput
              placeholder="Search"
              value={queryParams?.search}
              onChange={e =>
                handleChangeQueryParams(e?.target?.value, "search")
              }
            />
          </Grid>
          <Grid item>
            <PrimaryButton
              label="Add Group"
              startIcon={<AddIcon />}
              onClick={() => {
                setModalVisible(true);
                setModalPreload(null);
              }}
            />
          </Grid>
        </Grid>
        {loadingPage ? (
          <SkeletonComponent variant="wave" />
        ) : (
          <InnoTableV2
            isLoading={false}
            columns={columnTable}
            items={dataTable}
            page={queryParams?.page}
            rowsPerPage={queryParams?.size}
            totalPage={tableTotalPage}
            handleChangePage={(_, page) =>
              handleChangeQueryParams(page, "page")
            }
            handleChangeRowsPerPage={e =>
              handleChangeQueryParams(e?.target?.value, "size")
            }
            onRequestSort={sortBy => {
              handleChangeQueryParams(
                `${sortBy} ${
                  queryParams.sort === `${sortBy} asc` ? "desc" : "asc"
                }`,
                "sort"
              );
            }}
            isHaveAction
            renderAction={item => (
              <TableAction
                handleDelete={() => handleDelete(item)}
                handleEdit={() => {
                  setModalVisible(true);
                  setModalPreload(item.publisher_group_id);
                }}
                deleteConfirmation
                deleteConfirmationKey={item?.group_name}
              />
            )}
          />
        )}
        {modalVisible && (
          <ModalFormParameterGroup
            open={modalVisible}
            onClose={() => setModalVisible(false)}
            preload={modalPreload}
            getDataTable={getDataTable}
          />
        )}
      </Container>
    </Page>
  );
}

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Parameter",
    link: "/admin/parameter/group",
  },
  {
    label: "Group",
    active: true,
  },
];
const columnTable = [
  {
    name: "group_name",
    title: "Group Name",
    sort_key: "group_name",
    useSort: true,
  },
  {
    name: "composers",
    title: "Member",
    renderText: item => (
      <ArrayChip list={item?.map(composer => composer?.sure_name)} max={3} />
    ),
  },
  {
    name: "start_date",
    title: "Start Date",
    renderText: date => formatDate(date),
  },
  {
    name: "end_date",
    title: "End Date",
    renderText: date => formatDate(date),
  },
  {
    name: "is_active_flag",
    title: "Status",
    renderText: status => (
      <StatusChip
        type={status ? "success" : "danger"}
        label={status ? "Active" : "Inactive"}
      />
    ),
  },
];

export default ParameterGroup;
